/**
 * DartFormTable.tsx - DART Form Table Component
 *
 * Renders the table with radio buttons for DART form questions.
 */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import Table, {
  TableCell,
  TableRow,
  TableSectionHeader,
} from '@amzn/meridian/table';
import RadioButton from '@amzn/meridian/radio-button';
import {
  PageItem,
  AnsweredQuestionItem,
} from '../../../Util/Form/formData.types';
import { I18NContent } from '../../../api/CoachingNotes/schema';
import {
  DART_ANSWER_VALUES,
  TRANSLATION_IDS,
} from '../../../Util/Form/formData.constants';
import { csaFields } from '../../../Util/Form/dartFormProcessors';
import { getFormClickstreamTag1AndTag2 } from '../utils/helper';
import { styles } from './DartForm.styles';

interface DartFormTableProps {
  tableHeaders: I18NContent[];
  pages: PageItem[];
  questionsAnswered: Record<string, AnsweredQuestionItem>;
  reviewBarCriteria: Record<string, any>;
  isReviewedOrDelivered: boolean;
  ingress: string | null;
  ccxDetailsInfo: any;
  onQuestionAnswered: (item: string) => (data: AnsweredQuestionItem) => void;
}

const DartFormTable: React.FC<DartFormTableProps> = ({
  tableHeaders,
  pages,
  questionsAnswered,
  reviewBarCriteria,
  isReviewedOrDelivered,
  ingress,
  ccxDetailsInfo,
  onQuestionAnswered,
}) => {
  return (
    <Table headerRows={1} showDividers={true} spacing='small'>
      <TableRow highlightOnHover={false}>
        {tableHeaders.map((header: I18NContent) => {
          if (!header?.translationID) return null;
          return (
            <TableCell key={header.translationID}>
              <span>
                <FormattedMessage
                  id={header.translationID}
                  defaultMessage={header.defaultMessage}
                />
              </span>
            </TableCell>
          );
        })}
      </TableRow>
      {pages?.map((pageItem: PageItem, index: number) => {
        const key = pageItem.type;
        const points = pageItem.points;
        return [
          <TableSectionHeader key={`section-${key}-${index}`}>
            {key}
          </TableSectionHeader>,
          ...(points?.map(
            (point: { text: I18NContent }, pointIndex: number) => {
              if (!point?.text?.translationID) return null;
              const bullets = reviewBarCriteria?.[point.text.defaultMessage];
              return (
                <TableRow
                  key={`${key}-${point.text.translationID}-${pointIndex}`}
                >
                  <TableCell></TableCell>
                  <TableCell>
                    <span>
                      <FormattedMessage
                        id={point.text.translationID}
                        defaultMessage={point.text.defaultMessage}
                      />
                    </span>
                  </TableCell>
                  {bullets && (
                    <>
                      <TableCell
                        alignmentHorizontal='center'
                        key={`${point.text.defaultMessage}-meets-bar`}
                      >
                        <div className={styles.bulletInTableCell}>
                          <RadioButton
                            disabled={isReviewedOrDelivered}
                            checked={
                              questionsAnswered?.[point.text.defaultMessage]
                                ?.checkedValue === DART_ANSWER_VALUES.MEETS_BAR
                            }
                            onChange={() =>
                              onQuestionAnswered(point.text.defaultMessage)({
                                checkedValue: DART_ANSWER_VALUES.MEETS_BAR,
                                comments: '',
                              })
                            }
                            name='meets-bar'
                            value={DART_ANSWER_VALUES.MEETS_BAR}
                            {...csaFields(
                              'radio-button',
                              'meets-bar',
                              TRANSLATION_IDS.DART_MEETS_BAR,
                              ingress,
                              getFormClickstreamTag1AndTag2,
                              !!ccxDetailsInfo
                            )}
                          />
                        </div>
                      </TableCell>
                      <TableCell
                        alignmentHorizontal='center'
                        key={`${point.text.defaultMessage}-doesnt-meet-bar`}
                      >
                        <div className={styles.bulletInTableCell}>
                          <RadioButton
                            disabled={isReviewedOrDelivered}
                            checked={
                              questionsAnswered?.[point.text.defaultMessage]
                                ?.checkedValue ===
                              DART_ANSWER_VALUES.DOESNT_MEET_BAR
                            }
                            onChange={() =>
                              onQuestionAnswered(point.text.defaultMessage)({
                                checkedValue:
                                  DART_ANSWER_VALUES.DOESNT_MEET_BAR,
                                comments: '',
                              })
                            }
                            name='doesnt-meet-bar'
                            value={DART_ANSWER_VALUES.DOESNT_MEET_BAR}
                            {...csaFields(
                              'radio-button',
                              'doesnt-meet-bar',
                              TRANSLATION_IDS.DART_DOESNT_MEET_BAR,
                              ingress,
                              getFormClickstreamTag1AndTag2,
                              !!ccxDetailsInfo
                            )}
                          />
                        </div>
                      </TableCell>
                      <TableCell
                        alignmentHorizontal='center'
                        key={`${point.text.defaultMessage}-not-applicable`}
                      >
                        <div className={styles.bulletInTableCell}>
                          <RadioButton
                            disabled={isReviewedOrDelivered}
                            checked={
                              questionsAnswered?.[point.text.defaultMessage]
                                ?.checkedValue ===
                              DART_ANSWER_VALUES.NOT_APPLICABLE
                            }
                            onChange={() =>
                              onQuestionAnswered(point.text.defaultMessage)({
                                checkedValue: DART_ANSWER_VALUES.NOT_APPLICABLE,
                                comments: '',
                              })
                            }
                            name='not-applicable'
                            value={DART_ANSWER_VALUES.NOT_APPLICABLE}
                            {...csaFields(
                              'radio-button',
                              'not-applicable',
                              TRANSLATION_IDS.DART_NOT_APPLICABLE,
                              ingress,
                              getFormClickstreamTag1AndTag2,
                              !!ccxDetailsInfo
                            )}
                          />
                        </div>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            }
          ) || []),
        ];
      })}
    </Table>
  );
};

export default DartFormTable;
